import { AnnouncementDetails_announcement } from "@saleor/announcements/types/AnnouncementDetails";
import { AnnouncementUpdate_announcementUpdate_errors } from "@saleor/announcements/types/AnnouncementUpdate";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { maybe, splitDateTime } from "@saleor/misc";
import { useCreateAnnouncementValidation } from "@saleor/utils/validators/announcements";
import { hasNoError } from "@saleor/utils/validators/sales";
import { RawDraftContentState } from "draft-js";
import React from "react";

import AnnouncementContent from "../AnnouncementContent/AnnouncementContent";
import AnnouncementImage from "../AnnouncementImage";
import AnnouncementInfo from "../AnnouncementInfo";

export interface AnnouncementFormData {
  content: RawDraftContentState;
  image: string;
  title: string;
  startDate: string;
  endDate: string;
}

export interface AnnouncementDetailsPageProps {
  errors: AnnouncementUpdate_announcementUpdate_errors[];
  data: AnnouncementDetails_announcement;
  onBack: () => void;
  onDelete: () => void;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit(data: AnnouncementFormData, image: File);
}

const AnnouncementDetailsPage: React.FC<AnnouncementDetailsPageProps> = props => {
  const {
    onBack,
    onDelete,
    disabled,
    saveButtonBarState,
    onSubmit,
    data,
    errors
  } = props;

  const [imageToUpload, setImageToUpload] = React.useState(null);

  const [validator, setValidator] = React.useState({
    content: null,
    endDate: null,
    startDate: null,
    title: null
  });

  const initialForm: AnnouncementFormData = {
    content: data && JSON.parse(data.content),
    endDate:
      data && data.endDate
        ? splitDateTime(maybe(() => data.endDate, "")).date
        : null,
    image: maybe(() => data.image.url, null),
    startDate:
      data && data.startDate
        ? splitDateTime(maybe(() => data.startDate, "")).date
        : null,
    title: maybe(() => data.title, "")
  };

  const initialDescription = maybe<RawDraftContentState>(() =>
    JSON.parse(data.content)
  );

  const handleSubmit = event => {
    const validate = useCreateAnnouncementValidation(event);
    setValidator(validate);
    if (hasNoError(validate)) {
      onSubmit(event, imageToUpload);
    }
  };

  const breadcrumbDirectory = [
    {
      name: "Announcements",
      url: "/announcements"
    },
    {
      name: data && data.title
    }
  ];

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ data, change, hasChanged, submit }) => (
        <Container>
          <PageBreadcrumbNavigation items={breadcrumbDirectory} />
          <div
            style={{ display: "flex", flexDirection: "row", marginTop: "10px" }}
          >
            <div style={{ marginTop: "2px" }}>
              <PageBackNavigation onBack={onBack} />
            </div>
            <PageHeader title={data && data.title} />
          </div>

          <div>
            <AnnouncementInfo
              errors={errors}
              data={data}
              onChange={change}
              disabled={false}
              localError={validator}
            />
            <CardSpacer />
            <AnnouncementImage
              data={data}
              onChange={change}
              onImageUpload={file => setImageToUpload(file)}
            />
            <CardSpacer />
            <AnnouncementContent
              errors={errors}
              onChange={change}
              disabled={false}
              initialDescription={initialDescription}
              localError={validator}
            />
          </div>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            onCancel={onBack}
            onDelete={onDelete}
            onSave={submit}
            state={saveButtonBarState}
          />
        </Container>
      )}
    </Form>
  );
};

export default AnnouncementDetailsPage;
